import React, { useMemo } from 'react'
import { type UseFormReturn, Controller } from 'react-hook-form'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { H2, Line, Button, Flex } from '../../lib/components'
import type { FaqFormValues } from './faq-form-container'
import { CircularProgress } from '@mui/material'
import './faq-form.scss'

/** True when Quill/HTML has no visible text (handles `<p><br></p>`, whitespace, etc.). */
function isFaqAnswerEmpty(html: string | undefined | null): boolean {
  if (html === null || html === '') {
    return true
  }
  const text = new DOMParser().parseFromString(html as string, 'text/html').body.textContent ?? ''
  return text.trim() === ''
}

export type FaqFormPresenterProps = {
  form: UseFormReturn<FaqFormValues>
  isEditMode: boolean
  isSubmitting: boolean
  isLoading: boolean
  onSubmit: (data: FaqFormValues) => Promise<void>
  onCancel: () => void
}

export function FaqFormPresenter({
  form,
  isEditMode,
  isSubmitting,
  isLoading,
  onSubmit,
  onCancel,
}: FaqFormPresenterProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = form

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'link'],
          ['clean'],
        ],
      },
      clipboard: { matchVisual: false },
    }),
    [],
  )

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'blockquote',
    'link',
  ]

  return (
    <>
      {isLoading && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}
      <Flex variant='column-center'>
        <H2 classes={['mb-24']} text={isEditMode ? 'Edit FAQ' : 'Add FAQ'} />
        <form className='faq-admin-form' onSubmit={handleSubmit(onSubmit)} noValidate>
          <Line />
          <div className='faq-admin-form__field'>
            <label className='faq-admin-form__label' htmlFor='faq-question'>
              Question <span className='faq-admin-form__required'>*</span>
            </label>
            <input
              id='faq-question'
              type='text'
              className='faq-admin-form__input'
              autoComplete='off'
              {...register('question', { required: 'Question is required' })}
            />
            {errors.question ? (
              <p className='faq-admin-form__error'>{errors.question.message}</p>
            ) : null}
          </div>

          <div className='faq-admin-form__field'>
            <label className='faq-admin-form__label' htmlFor='faq-answer'>
              Answer <span className='faq-admin-form__required'>*</span>
            </label>
            <p className='faq-admin-form__hint'>
              Use the toolbar for bold, italic, lists, quotes, and links. Answers are saved as HTML
              for display on the site.
            </p>
            <div className='faq-admin-form__editor-wrapper' id='faq-answer'>
              <Controller
                name='answer'
                control={control}
                rules={{
                  validate: (v) => !isFaqAnswerEmpty(v) || 'Answer is required',
                }}
                render={({ field }) => (
                  <ReactQuill
                    theme='snow'
                    value={field.value ?? ''}
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                    onBlur={field.onBlur}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder='Write the answer…'
                  />
                )}
              />
            </div>
            {errors.answer ? (
              <p className='faq-admin-form__error'>{errors.answer.message}</p>
            ) : null}
          </div>

          <div className='faq-admin-form__field'>
            <label className='faq-admin-form__label' htmlFor='faq-category'>
              Category
            </label>
            <p className='faq-admin-form__hint'>
              Optional. FAQs with the same category are grouped together on the site.
            </p>
            <input
              id='faq-category'
              type='text'
              className='faq-admin-form__input'
              placeholder='e.g. General'
              autoComplete='off'
              {...register('category')}
            />
          </div>

          <div className='faq-admin-form__field'>
            <label className='faq-admin-form__label' htmlFor='faq-sort'>
              Sort order
            </label>
            <p className='faq-admin-form__hint'>Lower numbers appear first within a category.</p>
            <input
              id='faq-sort'
              type='number'
              className='faq-admin-form__input'
              min={0}
              step={1}
              {...register('sort_order', {
                valueAsNumber: true,
                min: { value: 0, message: 'Must be 0 or greater' },
                validate: (v) =>
                  (typeof v === 'number' && !Number.isNaN(v)) || 'Enter a valid number',
              })}
            />
            {errors.sort_order ? (
              <p className='faq-admin-form__error'>{errors.sort_order.message}</p>
            ) : null}
          </div>

          <div className='faq-admin-form__actions'>
            <Button
              text={isSubmitting ? 'Saving…' : 'Save'}
              variant='basic-btn'
              buttonType='submit'
              isDisabled={isSubmitting}
              dataTestId='faq-form-submit'
              handleClick={() => {}}
            />
            <Button
              text='Cancel'
              variant='secondary-btn'
              buttonType='button'
              handleClick={onCancel}
              isDisabled={isSubmitting}
            />
          </div>
        </form>
      </Flex>
    </>
  )
}
